import {
  CalendarSource,
  Member,
  MemberExternalAccountCalendarsResult,
} from '@vivport/backend/types';
import { getMemberDisplayName } from '@/types/member';

type CalendarSourceContext = {
  selectedMember: Member;
  calendarsByAccount: MemberExternalAccountCalendarsResult[];
};

export type CalendarRouteSelection = {
  selectedMemberCalendarSourceIds: string[];
  showHubClashes: boolean;
};

export type CalendarSourceCatalog = {
  defaultSource: CalendarSource;
  selectedSources: CalendarSource[];
  all: CalendarSource[];
  byId: Map<string, CalendarSource>;
};

function toSelectedMemberSourceId(memberId: number): string {
  return `selected-member:${memberId}`;
}

function toSelectedMemberProviderGoogleSourceId(input: {
  memberId: number;
  memberExternalAccountId: number;
  providerCalendarId: string;
}): string {
  return `selected-member-provider-google:${input.memberId}:${input.memberExternalAccountId}:${encodeURIComponent(input.providerCalendarId)}`;
}

function toAggregateFallbackSource(selectedMember: Member): CalendarSource {
  return {
    id: toSelectedMemberSourceId(selectedMember.id),
    kind: 'SELECTED_MEMBER',
    memberId: selectedMember.id,
    label: selectedMember.isPersonal ? 'My Calendar' : getMemberDisplayName(selectedMember),
    sublabel: 'Selected member aggregate',
    isPrimary: true,
  };
}

export function buildCalendarSourceCatalog(context: CalendarSourceContext | null): CalendarSourceCatalog | null {
  if (!context) {
    return null;
  }

  const rawProviderEntries = context.calendarsByAccount
    .filter((entry) => entry.source.provider === 'GOOGLE')
    .flatMap((entry) =>
      entry.items.map((calendar) => {
        const providerCalendarName = calendar.name?.trim().length
          ? calendar.name.trim()
          : calendar.providerCalendarId;
        return {
          memberExternalAccountId: entry.source.memberExternalAccountId,
          externalEmail: entry.source.externalEmail,
          providerCalendarId: calendar.providerCalendarId,
          providerCalendarName,
          isPrimary: calendar.isPrimary,
        };
      })
    );
  const nameCounts = rawProviderEntries.reduce((acc, item) => {
    const current = acc.get(item.providerCalendarName) ?? 0;
    acc.set(item.providerCalendarName, current + 1);
    return acc;
  }, new Map<string, number>());
  const selectedProviderSources = rawProviderEntries
    .map((entry) => {
      const includeEmailInLabel = entry.isPrimary || (nameCounts.get(entry.providerCalendarName) ?? 0) > 1;
      const label = includeEmailInLabel
        ? `${entry.providerCalendarName} (${entry.externalEmail})`
        : entry.providerCalendarName;
      return {
        id: toSelectedMemberProviderGoogleSourceId({
          memberId: context.selectedMember.id,
          memberExternalAccountId: entry.memberExternalAccountId,
          providerCalendarId: entry.providerCalendarId,
        }),
        kind: 'SELECTED_MEMBER' as const,
        memberId: context.selectedMember.id,
        label,
        sublabel: entry.isPrimary
          ? 'Google primary calendar'
          : `Google calendar - ${entry.externalEmail}`,
        isPrimary: entry.isPrimary,
        providerSelector: {
          provider: 'GOOGLE' as const,
          memberExternalAccountId: entry.memberExternalAccountId,
          providerCalendarId: entry.providerCalendarId,
        },
      } satisfies CalendarSource;
    })
    .sort((left, right) => {
      const leftIsPrimary = left.isPrimary;
      const rightIsPrimary = right.isPrimary;
      if (leftIsPrimary !== rightIsPrimary) {
        return leftIsPrimary ? -1 : 1;
      }
      if (left.label !== right.label) {
        return left.label.localeCompare(right.label);
      }
      return left.id.localeCompare(right.id);
    });
  const dedupedProviderSources = new Map<string, CalendarSource>();

  for (const source of selectedProviderSources) {
    if (!dedupedProviderSources.has(source.id)) {
      dedupedProviderSources.set(source.id, source);
    }
  }
  const selectedSources = [...dedupedProviderSources.values()];
  const aggregateFallbackSource = toAggregateFallbackSource(context.selectedMember);
  const all = selectedSources.length > 0
    ? selectedSources
    : [aggregateFallbackSource];
  const byId = new Map(all.map((source) => [source.id, source]));
  const defaultSource = selectedSources.length > 0
    ? byId.get(selectedSources.find((source) => source.isPrimary)?.id ?? selectedSources[0].id) ?? selectedSources[0]
    : aggregateFallbackSource;
  return {
    defaultSource,
    selectedSources,
    all,
    byId,
  };
}

export function readCalendarRouteSelection(
  searchParams: URLSearchParams,
  catalog: CalendarSourceCatalog | null
): CalendarRouteSelection {
  if (!catalog) {
    return {
      selectedMemberCalendarSourceIds: [],
      showHubClashes: false,
    };
  }

  const allowedSourceIds = new Set(catalog.byId.keys());
  const selectedMemberCalendarSourceIds = [...new Set(searchParams.getAll('selectedMemberSource'))]
    .filter((sourceId) => allowedSourceIds.has(sourceId))
    .sort((left, right) => left.localeCompare(right));
  const showHubClashes = searchParams.get('showHubClashes') === '1';

  if (selectedMemberCalendarSourceIds.length === 0) {
    selectedMemberCalendarSourceIds.push(catalog.defaultSource.id);
  }

  return {
    selectedMemberCalendarSourceIds,
    showHubClashes,
  };
}

export function createCalendarRouteSearchParams(selection: CalendarRouteSelection): URLSearchParams {
  const next = new URLSearchParams();
  const selectedMemberCalendarSourceIds = [...new Set(selection.selectedMemberCalendarSourceIds)]
    .filter((sourceId) => sourceId.length > 0)
    .sort((left, right) => left.localeCompare(right));
  for (const sourceId of selectedMemberCalendarSourceIds) {
    next.append('selectedMemberSource', sourceId);
  }
  if (selection.showHubClashes) {
    next.set('showHubClashes', '1');
  }
  return next;
}

export function toggleCalendarRouteSourceSelection(
  selection: CalendarRouteSelection,
  sourceId: string,
  fallbackSourceId: string
): CalendarRouteSelection {
  const current = new Set(selection.selectedMemberCalendarSourceIds);
  if (current.has(sourceId)) {
    current.delete(sourceId);
    if (current.size === 0) {
      current.add(fallbackSourceId);
    }
  } else {
    current.add(sourceId);
  }

  return {
    selectedMemberCalendarSourceIds: [...current].sort((left, right) => left.localeCompare(right)),
    showHubClashes: selection.showHubClashes,
  };
}
